import Image from 'next/image';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import logPostSignUpScreenCtaClicked from '@alltrails/amplitude/events/logPostSignUpScreenCtaClicked';
import logPostSignUpScreenDismissed from '@alltrails/amplitude/events/logPostSignUpScreenDismissed';
import logPostSignUpScreenViewed from '@alltrails/amplitude/events/logPostSignUpScreenViewed';
import Typography from '@alltrails/core/components/Typography';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ModalAriaLabelledByHeading from '@alltrails/modal/components/Modal/ModalAriaLabelledByHeading';
import ModalStep from '@alltrails/modal/components/Modal/ModalStep';
import styles from './AuthWelcomeStep.module.scss';
import navigateDesktopImg from './images/navigate-desktop.avif';
import navigateMobileImg from './images/navigate-mobile.avif';
import offlineMapsImg from './images/offline.avif';
import planImg from './images/save-trail.avif';
import discoverImg from './images/search.avif';

type Props = {
  onCloseRequest: () => void;
  onContinue: () => void;
  onTryPlus?: () => void;
};

// https://www.figma.com/design/of9BVPuMqzjbOWZMEDcGxX/New-User-Journey-2026?node-id=985-88330
export default function AuthWelcomeStep({ onCloseRequest, onContinue, onTryPlus }: Props) {
  const isMobile = useIsMobileSizedScreen();

  const features = [
    {
      image: discoverImg,
      key: 'discover',
      title: <FormattedMessage defaultMessage="Discover new trails" />,
      description: <FormattedMessage defaultMessage="Find nearby trails, top spots, and personalized picks for your next adventure" />
    },
    {
      image: planImg,
      key: 'plan',
      title: <FormattedMessage defaultMessage="Plan your next adventure" />,
      description: <FormattedMessage defaultMessage="Get trail details, reviews, and conditions — then save your favorites" />
    },
    {
      image: isMobile ? navigateMobileImg : navigateDesktopImg,
      key: 'track',
      title: <FormattedMessage defaultMessage="Track your activity" />,
      description: <FormattedMessage defaultMessage="Follow your route and track stats along the way with Navigate" />
    },
    {
      image: offlineMapsImg,
      key: 'offline',
      title: <FormattedMessage defaultMessage="Take maps offline" />,
      description: <FormattedMessage defaultMessage="Download maps ahead of time to stay on route without cell service" />
    }
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    logPostSignUpScreenViewed();
  }, []);

  return (
    <ModalStep
      contentsClassName={onTryPlus ? styles.modalContentsChoice : styles.modalContents}
      footerClassName={styles.footer}
      hideBackButton
      onCloseRequest={() => {
        logPostSignUpScreenDismissed();
        onCloseRequest();
      }}
      buttons={
        onTryPlus
          ? {
              fill: true,
              primaryButton: {
                text: <FormattedMessage defaultMessage="Continue to trail" />,
                onClick: () => {
                  logPostSignUpScreenCtaClicked({ cta: 'continue to trail' });
                  onContinue();
                }
              },
              defaultButton: {
                text: <FormattedMessage defaultMessage="Try Plus for {trialDuration} days" values={{ trialDuration: 7 }} />,
                onClick: () => {
                  logPostSignUpScreenCtaClicked({ cta: 'try plus' });
                  onTryPlus();
                }
              }
            }
          : {
              fill: true,
              primaryButton: {
                text: <FormattedMessage defaultMessage="Continue to trail" />,
                onClick: () => {
                  logPostSignUpScreenCtaClicked({ cta: 'continue to trail' });
                  onContinue();
                }
              }
            }
      }
    >
      <div className={styles.content}>
        <ModalAriaLabelledByHeading as="h2" size="400">
          <FormattedMessage defaultMessage="Explore with AllTrails" />
        </ModalAriaLabelledByHeading>
        <div className={styles.features}>
          {features.map(({ image, key, title, description }) => (
            <div key={key} className={styles.featureRow}>
              <Image alt="" className={styles.featureImage} height={72} src={image} width={72} />
              <div className={styles.featureCopy}>
                <Typography variant="heading200">{title}</Typography>
                <Typography color="primary" variant="text100">
                  {description}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModalStep>
  );
}
